export interface Sorter<T> {
  function?: (item: T) => any;
  field?: keyof T;
  isDescending?: boolean;
}

/**
 * Sorts the provided array using an array of sorters. This allows for sorting by different criteria.
 *
 * FYI sorting dates as strings (e.g. in ISO 8601 format) should work as-is without needing to use the sorter's
 * function property.
 *
 * @example
 * ```
 * sort(
 *   [{ age: 7, name: 'Bob' }, { age: 7, name: 'Bart'}, { age: 8, name: 'Candace'}]
 *   { field: 'age', isDescending: true },
 *   { field: 'name' }
 * );
 *
 * // Results in: Candace (Age 8), Bart (Age 7), Bob (Age 7)
 * ```
 * @param array array to be sorted. This array is updated inplace
 * @param sorters array of sorters that will be used to sort the array. If any elements match, the next sorter in the
 * list will be used
 * @returns the sorted array (which is the same as the input array)
 */
export function sort<T>(array: T[], ...sorters: Sorter<T>[]): T[] {
  const sortValue = (a: any, b: any, isDescending: boolean): number => {
    let result = 0;

    // If both a and b are null/undefined, treat them as being equal
    if ((a === undefined || a === null) && (b === undefined || b === null)) {
      return 0;
    }

    // Check the different supported types of objects, handle cases where one may be null/undefined
    if (typeof a === "string" || typeof b === "string") {
      result = (a ?? "").localeCompare(b ?? "");
    } else if (Number.isFinite(a) || Number.isFinite(b)) {
      result = (a ?? 0) - (b ?? 0);
    } else if (typeof a === "boolean" || typeof b === "boolean") {
      result = (a ?? false) - (b ?? false);
    } else {
      throw new Error(`Sorter does not support compare data types: ${typeof a} and ${typeof b}`);
    }

    // If performing a descending sort, flip the result
    if (isDescending) result *= -1;

    return result;
  };

  const sortFunc = (a: T, b: T, index: number): number => {
    if (index >= sorters.length) return 0;
    if (!sorters[index]) return 0;

    // Apply the sort logic to each element
    const sorter = sorters[index];
    let valA: any;
    let valB: any;

    if (sorter.field) {
      valA = a[sorter.field];
      valB = b[sorter.field];
    } else if (sorter.function) {
      valA = sorter.function(a);
      valB = sorter.function(b);
    } else {
      throw new Error("Sorter must define either a field or function for sorting");
    }

    const result = sortValue(valA, valB, !!sorter.isDescending);

    // If the items are equal, try the next sorter. Otherwise return the sort result
    if (result === 0) return sortFunc(a, b, index + 1);
    else return result;
  };

  return array.sort((a, b) => sortFunc(a, b, 0));
}
